import classnames from 'classnames';
import type { FunctionComponent, ReactNode } from 'react';

import { forgeTestId } from '@/core/tamagoshi/utils/forgeTestId';

import { Button, type ButtonProps } from '../Button/Button';

import styles from './TabItem.module.scss';

type HrefOrValue =
  | {
      /** a link associated with the menu - value or href is required */
      href: string;
    }
  | {
      /** a value associated with the menu - value or href is required */
      value: string;
    };

export interface TabItemProps extends Omit<ButtonProps, 'ref' | 'children'> {
  /** Any node that has to be wrapped */
  children?: ReactNode;
  /** To override the style */
  className?: string;
  /** To set selected state */
  isSelected?: boolean;
  /** To set word wrap */
  isWordWrap?: boolean;
  /**
   * Identifier for test purpose.
   * Will add a `data-testid` attribute on root & button elements.
   */
  testId?: string;
  /** The width of the component */
  width?: number;
}

type TabItemExtendedProps = TabItemProps & HrefOrValue;

export const TabItem: FunctionComponent<TabItemExtendedProps> = ({
  children,
  className,
  href,
  isSelected = false,
  isWordWrap = false,
  testId,
  value,
  width,
  ...restProps
}) => {
  const rootStyle = width ? { width: `${width}%` } : undefined;
  const rootClassNames = classnames(styles.tabItem, className);
  const buttonClassNames = classnames(styles.tabButton, {
    [styles.tabButtonNoWrap]: !isWordWrap,
  });

  return (
    <li
      className={rootClassNames}
      style={rootStyle}
      data-testid={testId}
      role="tab"
    >
      <Button
        {...restProps}
        aria-selected={isSelected ? true : undefined}
        variant="none"
        theme="none"
        className={buttonClassNames}
        value={value}
        href={href}
        testId={forgeTestId(testId, 'cta')}
      >
        {isWordWrap ? (
          children
        ) : (
          <span className={styles.tabButtonNoWrap}>{children}</span>
        )}
      </Button>
    </li>
  );
};

TabItem.displayName = 'TabItem';
